<template>
	<div class="flex flex-col">
		<!-- Company / job -->
		<div class="flex flex-col">
			<span
				v-if="isShowCompany"
				@click="$router.push(`/${$i18n.locale}/company/${companySlug}`)"
				class="font-medium text-xl md:font-normal md:text-base mb-1 text-dj-gray-dark cursor-pointer"
			>
				{{ company }}
			</span>
			<span class="text-sm md:text-base mb-6 md:mb-1 text-dj-gray-dark md:hidden">
				{{ location }}
			</span>
			<h1 class="block font-medium text-xl mb-4 md:text-4xl md:leading-10">
				{{ name }}
			</h1>
		</div>
		<!-- Job tags -->
		<div class="text-sm md:text-base text-dj-gray-dark flex flex-wrap max-w-[350px] md:max-w-full gap-y-2">
			<span class="block" v-for="(tag, index) in tags" :key="index">
				{{ tag[locale] }}
				<span v-if="index != tags.length - 1" class="mx-2">|</span>
			</span>
			<template v-for="(office, index) in parsedJobOffices" :key="index">
				<template v-if="office && office.name && office.name[locale]">
					<span class="mx-2">|</span>
					<span class="block">
						{{ office.name[locale] }}
					</span>
					<span class="block text-dj-gray ml-1" v-if="office?.description && isRegion('hu')">
						{{
							office.description[locale] !== undefined && office.description[locale] !== null
								? office.description[locale]
								: ""
						}}
					</span>
				</template>
			</template>

			<template v-if="jobBadges && jobBadges.some((badge) => badge.slug === 'tavmunka')">
				<span class="mx-2">|</span>
				<span class="block">
					{{ jobBadges.find((badge) => badge.slug === "tavmunka")?.name[locale] }}
				</span>
			</template>

			<template v-if="jobBadges && jobBadges.some((badge) => badge.slug === 'home-office')">
				<span class="mx-2">|</span>
				<span class="block">
					{{ jobBadges.find((badge) => badge.slug === "home-office")?.name[locale] }}
				</span>
			</template>

			<template v-if="jobBadges && jobBadges.some((badge) => badge.slug === 'hibrid-munkavegzes')">
				<span class="mx-2">|</span>
				<span class="block">
					{{ jobBadges.find((badge) => badge.slug === "hibrid-munkavegzes")?.name[locale] }}
				</span>
			</template>
		</div>
		<!-- Salary -->
		<div class="mt-[30px]">
			<div v-if="!salary.notSpecified && salary.salary" class="flex flex-col">
				<span class="text-sm text-dj-gray mb-1">
					{{ $t(`job.${salary.amountIn}`) }}
					<template v-if="salary.salaryPeriod === 'hour'">{{ $t("job.perHour") }}</template>
					<template v-else-if="salary.salaryPeriod === 'month'">{{ $t("job.perMonth") }}</template>
					<template v-else>{{ salary.salary.includes("-") ? "" : $t("job.salary") }}</template>
				</span>
				<div class="flex items-center">
					<span>{{ salary.salary }} {{ salary.currency.toLocaleUpperCase() }}</span>
					<BaseTagPill @click="scrollToBonus" v-if="hasBonus" class="ml-4 cursor-pointer" size="small">
						+{{ $t("job.bonus") }}
					</BaseTagPill>
				</div>
			</div>
			<div v-else class="flex flex-col">
				<span class="text-sm text-dj-gray mb-1">{{ capitalizeFirstLetter($t("job.salaryRange")) }}</span>
				<div class="flex">
					<span>{{ $t("job.notSpecified") }}</span>
					<BaseTagPill @click="scrollToBonus" v-if="hasBonus" class="ml-4 cursor-pointer" size="small">
						+{{ $t("job.bonus") }}
					</BaseTagPill>
				</div>
			</div>
		</div>
	</div>
</template>

<script lang="ts">
import { defineComponent, PropType, ref, computed } from "vue";
import { useI18n } from "vue-i18n";
import { capitalizeFirstLetter } from "~~/utils";
import { isRegion } from "@@/composables/helpers";
import { type JobItem } from "@@/composables/useJob";

type salaryType = {
	notSpecified: boolean;
	currency?: string;
	amountIn?: string;
	salaryPeriod?: string;
	salary?: string;
	other?: string;
	bonus?: Record<string, string>;
};

type OfficeTag = {
	id: number;
	slug: string;
	name: Record<string, string>;
	type: string;
	description?: Record<string, string | null>;
};

export default defineComponent({
	name: "JobProfileInfo",
	props: {
		jobData: {
			type: Object as PropType<JobItem>,
			default: null,
		},
		company: {
			type: String,
			default: "Company",
		},
		companySlug: {
			type: String,
		},
		name: {
			type: String,
			default: "job",
		},
		tags: {
			type: Array,
		},
		jobOffices: {
			type: [Array, Object] as PropType<Array<OfficeTag> | { data: Array<OfficeTag> }>,
		},
		salary: {
			type: Object as PropType<salaryType>,
			required: true,
		},
		location: {
			type: String,
			default: "company address",
		},
		jobBadges: {
			type: Array,
			default: () => [],
		},
	},
	setup(props) {
		const { locale } = useI18n();

		const isShowCompany = ref<boolean>(true);
		if (props.jobData) {
			isShowCompany.value = props.jobData.company_id !== null;
		}

		const parsedJobOffices = computed(() => {
			if (!props.jobOffices) return [];
			if (Array.isArray(props.jobOffices)) return props.jobOffices;
			if ("data" in props.jobOffices && Array.isArray(props.jobOffices.data)) return props.jobOffices.data;
			return [];
		});

		const hasBonus = computed(() => {
			const bonus = props.salary?.bonus;
			if (!bonus || typeof bonus !== "object") return false;
			return Object.values(bonus).some((value) => typeof value === "string" && value.trim().length > 0);
		});

		function scrollToBonus() {
			const salaryBox = document.getElementById("jobProfileSalaryBox");
			if (salaryBox) {
				window.scrollTo({ top: salaryBox.offsetTop - 80, left: 0, behavior: "smooth" });
			}
		}

		return {
			locale,
			scrollToBonus,
			capitalizeFirstLetter,
			isRegion,
			isShowCompany,
			parsedJobOffices,
			hasBonus,
		};
	},
});
</script>
